/* eslint-disable react-hooks/exhaustive-deps */
import { Button, Empty, Input, Spin, Table, Tooltip } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { Eye, Pencil } from 'lucide-react'
import { ChangeEvent, Dispatch, FC, SetStateAction, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUser } from '../../contexts'
import { useFiltersV2 } from '../../contexts/FiltersContextV2'
import useInputFilter from '../../hooks/useInputFilter'
import {
  BlocktrainDetail,
  BlockTrainStatusEnum,
  Material,
  RailcarDetail,
  RoleEnum,
  SupplierPurchaseOrder,
} from '../../types'
import { formatNumberWithThousands } from '../../utils/numbers'
import debounce from '../../utils/debounce'
import { ActionsCellContainer, StyledRow, StyledSelect } from '../StyledComponents'

const filterInputStyle = { flex: 1, minWidth: '10rem' }

interface RailcarsTabPaneContentProps {
  blocktrain: BlocktrainDetail
  allowedEditingStatus: BlockTrainStatusEnum[]
  setSelectedRailcar: Dispatch<SetStateAction<RailcarDetail | undefined>>
  blocktrainsIsLoading?: boolean
  setAddModalIsOpen?: Dispatch<SetStateAction<boolean>>
  purchaseOrders?: SupplierPurchaseOrder[]
  materialsOptions?: {
    label: string
    value: number
  }[]
  purchaseOrdersIsLoading?: boolean
}

const RailcarsTabPaneContent: FC<RailcarsTabPaneContentProps> = ({
  blocktrain,
  blocktrainsIsLoading,
  allowedEditingStatus,
  setSelectedRailcar,
  setAddModalIsOpen,
  purchaseOrdersIsLoading,
}) => {
  const { t } = useTranslation()
  const { filters, handleFilterChange, handleResetFilters, filtersToShow } = useFiltersV2()

  const { state: user } = useUser()

  const currentBloctrainId = blocktrain.id
  const userRole = user?.roles?.[0]?.name

  const filteredRailcars = useMemo(() => {
    if (filtersToShow) {
      return blocktrain.railCars.filter(railcar => {
        let railcarIdFilter = true
        if (filtersToShow.railcarId) {
          railcarIdFilter = String(railcar.railCarId).includes(String(filtersToShow.railcarId))
        }

        let materialFilter = true
        if (!!(filtersToShow.material as number[])?.length) {
          materialFilter = (filtersToShow.material as number[]).includes(railcar.material?.id ?? -1)
        }

        let purchaseOrdersFilter = true
        if (!!(filtersToShow.purchaseOrders as number[])?.length) {
          purchaseOrdersFilter = (filtersToShow.purchaseOrders as number[]).includes(
            railcar.purchaseOrder?.id ?? -1,
          )
        }

        let deliveryNoteFilter = true
        if (!!(filtersToShow.deliveryNote as number[])?.length) {
          deliveryNoteFilter = (filtersToShow.deliveryNote as number[]).includes(
            railcar.deliveryNote as unknown as number,
          )
        }

        return railcarIdFilter && materialFilter && purchaseOrdersFilter && deliveryNoteFilter
      })
    }

    return blocktrain.railCars
  }, [blocktrain, filtersToShow, currentBloctrainId])

  const rowClassName = (_record: unknown, index: number) =>
    index % 2 === 0 ? 'zebra-even' : 'zebra-odd'

  const columnsBlocktrain: ColumnsType = useMemo(
    () => [
      {
        title: t('number'),
        dataIndex: 'railCarNumber',
        key: 'numberId',
        render: (railCarNumber: string) => `${blocktrain?.id} - ${railCarNumber}`,
        sorter: (a, b) =>
          `${blocktrain?.id} - ${a.railCarNumber}`.localeCompare(
            `${blocktrain?.id} - ${b.railCarNumber}`,
          ),
      },
      {
        title: t('railcar_ID'),
        dataIndex: 'railCarId',
        key: 'railcarId',
        render: (railcarId: string) => railcarId ?? '-',
        sorter: (a, b) => (a.railCarId ?? '').localeCompare(b.railCarId ?? ''),
      },
      {
        title: t('material'),
        dataIndex: 'material',
        key: 'material',
        render: (material: Material) =>
          material ? `${material?.nameShort} - ${material.sapCode.replace(/^(0+)/g, '')}` : '-',
        sorter: (a, b) =>
          (a.material
            ? `${a.material.nameShort} - ${a.material.sapCode.replace(/^(0+)/g, '')}`
            : ''
          ).localeCompare(
            b.material
              ? `${b.material.nameShort} - ${b.material.sapCode.replace(/^(0+)/g, '')}`
              : '',
          ),
      },
      {
        title: t('po_number'),
        dataIndex: 'purchaseOrder',
        key: 'purchaseOrder',
        render: (purchaseOrder: SupplierPurchaseOrder) =>
          purchaseOrder?.PurchaseDocumentNumber ?? '-',
        sorter: (a, b) =>
          (a.purchaseOrder?.PurchaseDocumentNumber ?? '').localeCompare(
            b.purchaseOrder?.PurchaseDocumentNumber ?? '',
          ),
      },
      {
        title: t('gross_weight'),
        dataIndex: 'grossTn',
        key: 'grossWeight',
        render: (grossTn: string) => formatNumberWithThousands(grossTn),
        sorter: (a, b) => (parseFloat(a.grossTn) || 0) - (parseFloat(b.grossTn) || 0),
      },
      {
        title: t('tare_weight'),
        dataIndex: 'tareTn',
        key: 'tareWeight',
        render: (tareTn: string) => formatNumberWithThousands(tareTn),
        sorter: (a, b) => (parseFloat(a.tareTn) || 0) - (parseFloat(b.tareTn) || 0),
      },
      {
        title: t('net_kg_'),
        dataIndex: 'netTn',
        key: 'netWeight',
        render: (netTn: string) => formatNumberWithThousands(netTn),
        sorter: (a, b) => (parseFloat(a.netTn) || 0) - (parseFloat(b.netTn) || 0),
      },
      {
        title: t('delivery_note-column'),
        dataIndex: 'deliveryNote',
        key: 'deliveryNote',
        render: (deliveryNote: string) => deliveryNote ?? '-',
        sorter: (a, b) => (a.deliveryNote ?? '').localeCompare(b.deliveryNote ?? ''),
      },
      {
        title: t('actions'),
        key: 'actions',
        align: 'center',
        dataIndex: 'id',
        className: 'column-actions',
        render: (_id, railcar) => (
          <ActionsCellContainer>
            <Button
              className="icon-button"
              onClick={() => {
                setSelectedRailcar(railcar)
              }}
            >
              {userRole === 'SUPPLIER' ? (
                allowedEditingStatus.includes(blocktrain!.status) ? (
                  <Pencil />
                ) : (
                  <Eye />
                )
              ) : (
                <Eye />
              )}
            </Button>
          </ActionsCellContainer>
        ),
      },
    ],
    [blocktrain],
  )

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleFilterOptions = (input: string, option: any) => {
    if (option.label) return option.label.toString().toLowerCase().includes(input.toLowerCase())
    return true
  }

  const handleRailcarId = debounce((e: ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('railcarId')(e.target.value)
  }, 500)

  useInputFilter(['railcarId'])

  const uniqueMaterials = useMemo(() => {
    const uniqueMaterialsMap = new Map()
    filteredRailcars.forEach(({ material }) => {
      if (material) {
        uniqueMaterialsMap.set(material.id, {
          value: material.id,
          label: `${material.nameShort} - ${material.sapCode.replace(/^(0+)/g, '')}`,
        })
      }
    })
    return Array.from(uniqueMaterialsMap.values())
  }, [filteredRailcars])

  const uniquePurchaseOrders = useMemo(() => {
    const uniqueOrders = new Map()
    filteredRailcars.forEach(({ purchaseOrder }) => {
      if (purchaseOrder) {
        uniqueOrders.set(purchaseOrder.PurchaseDocumentNumber, {
          value: purchaseOrder.id,
          label: `${purchaseOrder.PurchaseDocumentNumber}${
            purchaseOrder.material?.nameShort ? ` - ${purchaseOrder.material.nameShort}` : ''
          }`,
        })
      }
    })
    return Array.from(uniqueOrders.values())
  }, [filteredRailcars])

  return (
    <>
      <StyledRow className="sticky-options">
        {user?.roles?.some(role => role.name === RoleEnum.SUPPLIER) &&
        allowedEditingStatus.includes(blocktrain?.status) &&
        setAddModalIsOpen ? (
          <Button
            type="primary"
            onClick={() => setAddModalIsOpen(true)}
            className="search-container"
          >
            {t('add_railcar')}
          </Button>
        ) : null}
        <div className="search-container" style={filterInputStyle}>
          <Input
            placeholder={t('railcar_ID')}
            type="string"
            onChange={handleRailcarId}
            allowClear
            id="railcarId"
          />
        </div>
        <StyledSelect
          style={filterInputStyle}
          mode="multiple"
          allowClear
          placeholder={t('material')}
          options={uniqueMaterials}
          onChange={handleFilterChange('material')}
          value={filters?.material}
          maxTagCount="responsive"
        />
        <StyledSelect
          style={filterInputStyle}
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          placeholder={t('po_number')}
          options={uniquePurchaseOrders}
          filterOption={handleFilterOptions}
          onChange={handleFilterChange('purchaseOrders')}
          loading={purchaseOrdersIsLoading}
          value={filters?.purchaseOrders}
        />
        <StyledSelect
          style={filterInputStyle}
          mode="multiple"
          placeholder={t('delivery_note')}
          options={[
            ...Array.from(new Set(filteredRailcars.map(({ deliveryNote }) => deliveryNote))).map(
              value => ({ value }),
            ),
          ]}
          onChange={handleFilterChange('deliveryNote')}
          value={filters?.deliveryNote}
          maxTagCount="responsive"
          allowClear
          filterOption={handleFilterOptions}
        />
        <Tooltip placement="bottomRight" title={t('clean_filters')}>
          <Button
            onClick={handleResetFilters}
            style={{ backgroundColor: 'unset', minWidth: 'fit-content' }}
          >
            {t('clean_filters')}
          </Button>
        </Tooltip>
      </StyledRow>
      <StyledRow className="ticket-handler">
        <div style={{ flex: 1 }} />
        {blocktrainsIsLoading ? <Spin /> : <div style={{ flex: 1 }} />}
      </StyledRow>
      <Table
        columns={columnsBlocktrain}
        dataSource={filteredRailcars?.sort((a, b) => a.id - b.id)} //ordeno por id asc
        rowKey="id"
        rowClassName={rowClassName}
        pagination={false}
        className="table-footer "
        locale={{
          emptyText: <Empty description={t('no_data_found')} />,
        }}
        scroll={{
          x: 'max-content',
        }}
      />
    </>
  )
}

export default RailcarsTabPaneContent
